/**
 * ErrorBoundary
 *
 * Captura erros não-tratados em componentes filhos e mostra UI de fallback
 * ao invés de deixar a tela branca.
 *
 * Uso:
 * <ErrorBoundary>
 *   <Dashboard />
 * </ErrorBoundary>
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import styles from './ErrorBoundary.module.css';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Gerar ID único para o erro (para logging/rastreamento)
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log para console (DEV)
    console.error(
      `%c[ErrorBoundary] ${errorId}`,
      'color: #ff6b6b; font-weight: bold;',
      error,
      errorInfo
    );

    // TODO: Enviar para Sentry (opcional em V2)
    // Sentry.captureException(error, { contexts: { errorInfo } });
  }

  handleReload = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
    window.location.reload();
  };

  render() {
    const { hasError, error, errorId } = this.state;

    if (hasError) {
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className={styles.errorIcon}
            />

            <h1 className={styles.errorTitle}>Oops! Algo deu errado</h1>

            <p className={styles.errorMessage}>
              Desculpe, encontramos um erro inesperado.
              Tente recarregar a página pra voltar ao normal.
            </p>

            {errorId && (
              <p className={styles.errorId}>
                Código do erro: <code>{errorId}</code>
                <br />
                <small>(compartilhe isto com o suporte se o problema persistir)</small>
              </p>
            )}

            {process.env.NODE_ENV === 'development' && error && (
              <details className={styles.errorDetails}>
                <summary>Detalhes técnicos (DEV)</summary>
                <pre>
                  {error.toString()}
                  {'\n\n'}
                  {error.stack}
                </pre>
              </details>
            )}

            <button
              className={styles.reloadButton}
              onClick={this.handleReload}
            >
              Recarregar Página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
